<template>
    <div v-if="price && price > 0" class="mt-2 flex items-center justify-between">
        <div class="flex flex-col">
            <span class="flex items-baseline gap-2">
                <span
                    v-if="isCheapestConfiguratorSize && !hasRecommendedPrice"
                    class="text-2xl font-extrabold lowercase"
                    v-text="$t('general.prices.starting_from.label')"
                    data-test-id="pdp-price-starting-from"
                />
                <span class="price text-4xl font-extrabold" v-text="formatPrice(price / 100)" data-test-id="pdp-price" />
            </span>
            <span v-if="hasRecommendedPrice" class="flex items-center gap-1">
                <span v-text="$t('general.prices.from.label')" />
                <span v-text="formatPrice(recommendedPrice / 100)" data-test-id="pdp-recommended-price" class="line-through" />
            </span>
            <span
                v-if="productStore.isMediaclipConfiguratorProduct"
                class="mt-1 text-sm text-gray-600"
                v-text="productDisplayName(productStore.selectedProduct.product)"
                data-test-id="pdp-price-size"
            />
        </div>

        <DiscountBadge :discount="productPageStore.configurableProduct.discount" size="3xl" />
    </div>
</template>

<script setup lang="ts">
import DiscountBadge from '@/components/badges/DiscountBadge.vue';
import { useFormatPrice } from '@/composables/useFormatPrice';
import { productDisplayName } from '@/lib/products/filter';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductStore } from '@/stores/products/ProductStore';
import type { ConfigurableProductProductObject } from '@/types/generated';
import _ from 'lodash';
import { computed } from 'vue';

const formatPrice = useFormatPrice();

const productPageStore = useProductPageStore();
const productStore = useProductStore();

const price = computed(() =>
    productStore.selectedProduct.product.data.product_price ? productStore.selectedProduct.product.data.product_price * 100 : 0,
);
const recommendedPrice = computed(() =>
    productStore.selectedProduct.product.data.product_price_recommended
        ? productStore.selectedProduct.product.data.product_price_recommended * 100
        : 0,
);

const hasRecommendedPrice = computed<boolean>(() => recommendedPrice.value > price.value);

const cheapestPrice = computed<number | undefined>(() =>
    _.min(_.compact(_.map(productStore.availableSizes, (size: ConfigurableProductProductObject) => size.product.data.product_price))),
);

// A configurator product starts at its cheapest size, so that price reads as a starting price.
const isCheapestConfiguratorSize = computed<boolean>(
    () =>
        productStore.isMediaclipConfiguratorProduct &&
        cheapestPrice.value !== undefined &&
        productStore.selectedProduct.product.data.product_price === cheapestPrice.value,
);
</script>
